import { useCallback, useEffect, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'

import { useTriggerOnboarding } from '@/features/dashboard/hooks/useTriggerOnboarding'
import { useTriggerSaveToDraft } from '@/features/dashboard/hooks/useTriggerSaveToDraft'
import { SaveToDraftResult } from '@/core/hooks-and-services/onboardedSensorsService'
import { Sensor } from '@/features/dashboard/models'
import { QueryKeys } from '@/core/models/enum'
import {
    plantsAtom,
    devicesAtom,
    projectsAtom,
    selectedPlantAtom,
    selectedDeviceAtom,
    selectedProjectAtom,
    selectedFilterStatusAtom,
    scannedDataAtom,
    activeStepAtom,
    inputValuesAtom,
    sensorDataAtom,
    resetInputValuesAtom,
    updateSensorDataAtom,
    addSensorDataAtom,
    showErrorSelectionBarAtom,
    showErrorFormAtom,
    showImageErrorsAtom,
    showErrorOverviewAtom,
    errorFormVendorSerialNumberAtom,
    imagesAtom,
    descriptionsAtom,
    isOfflineAtom,
} from '@/core/store/globalStore'
import { AxiosError } from 'axios'
import { InstalledSensorsDataDto, InstalledSensorStatus, ResponseObject } from '@/core/models/types'
import { getErrorMessage } from '@/core/models/errorHandling'
import { getInstalledSensorsByVendorAndModel } from '@/api/apiService'

export const COORDINATES_REGEX = /^(E\d+mm|N\d+mm|U\d+mm)(,(E\d+mm|N\d+mm|U\d+mm))*$/

// Users sometimes paste coordinates with spaces, e.g. "E302788mm, N93157mm, U526824mm".
// The canonical format has no whitespace, so strip it all and accept the value.
export const normalizeCoordinates = (value: string): string => value.replace(/\s+/g, '')

// Reads the same plant/device/project/sensor-data atoms as useOnboardingStepContent so both hooks stay
// in sync without passing values between them.
export const useOnboardingStepper = ({
    onClose,
    onReopen,
    onOpenDraft,
    onOpenCreatedSensor,
}: {
    onClose: () => void
    onReopen?: () => void
    onOpenDraft?: (serialNumber: string, plantShortName?: string) => void
    onOpenCreatedSensor?: (serialNumber: string, plantShortName?: string) => void
}) => {
    const plantList = useAtomValue(plantsAtom)
    const deviceList = useAtomValue(devicesAtom)
    const projectList = useAtomValue(projectsAtom)

    const [selectedPlant, setSelectedPlant] = useAtom(selectedPlantAtom)
    const [selectedDevice, setSelectedDevice] = useAtom(selectedDeviceAtom)
    const [selectedProject, setSelectedProject] = useAtom(selectedProjectAtom)
    const [images] = useAtom(imagesAtom)
    const [descriptions] = useAtom(descriptionsAtom)
    const setShowErrorSelectionBar = useSetAtom(showErrorSelectionBarAtom)
    const [, setShowErrorForm] = useAtom(showErrorFormAtom)
    const [, setErrorFormVendorSerialNumber] = useAtom(errorFormVendorSerialNumberAtom)
    const [, setShowImageErrors] = useAtom(showImageErrorsAtom)
    const [showErrorOverview, setShowErrorOverview] = useAtom(showErrorOverviewAtom)
    const isOffline = useAtomValue(isOfflineAtom)

    const setScannedData = useSetAtom(scannedDataAtom)
    const [activeStep, setActiveStep] = useAtom(activeStepAtom)
    const [inputValues, setInputValues] = useAtom(inputValuesAtom)
    const [sensorData, setSensorData] = useAtom(sensorDataAtom)

    const resetInputValues = useSetAtom(resetInputValuesAtom)
    const updateSensorData = useSetAtom(updateSensorDataAtom)
    const addSensorData = useSetAtom(addSensorDataAtom)
    const setSelectedStatus = useSetAtom(selectedFilterStatusAtom)

    const { onboard, isLoading, isSuccess, isError, isQueuedOffline } = useTriggerOnboarding()
    const { saveToDraft, isDraftLoading } = useTriggerSaveToDraft()
    const queryClient = useQueryClient()

    const [draftSuccessSerialNumber, setDraftSuccessSerialNumber] = useState<string | null>(null)
    const [draftError, setDraftError] = useState<string | null>(null)
    const [draftQueuedOffline, setDraftQueuedOffline] = useState(false)
    const [createdSensorSerialNumber, setCreatedSensorSerialNumber] = useState<string | null>(null)
    // The onboarded-sensors list must be refetched before navigating, so this drives a spinner instead of a frozen button.
    const [isNavigatingToDraft, setIsNavigatingToDraft] = useState(false)
    const [isNavigatingToCreatedSensor, setIsNavigatingToCreatedSensor] = useState(false)

    useEffect(() => {
        if (isError) {
            setCreatedSensorSerialNumber(null)
            onClose()
        }
    }, [isError])

    const firstStepComplete = () => {
        const isAvailable = selectedPlant && selectedDevice && selectedProject && inputValues.VendorSerialNumber
        return Boolean(isAvailable)
    }

    const formatSensor = (s: InstalledSensorsDataDto) => {
        const parts = [
            s.plant && `Plant: ${s.plant}`,
            s.area && `Area: ${s.area}`,
            s.tag && `Tag: ${s.tag}`,
            s.status && `Status: ${s.status}`,
        ].filter(Boolean)
        return parts.length ? ` (${parts.join(', ')})` : ''
    }

    // Returns a display error when the serial number is taken or not pre-provisioned, otherwise null.
    const validateVendorSerialNumber = async (): Promise<string | null> => {
        try {
            const checkResult = await getInstalledSensorsByVendorAndModel(
                inputValues.VendorSerialNumber || '',
                selectedDevice?.key || '',
                inputValues.SensorDbKey || ''
            )

            const existingSensors = checkResult.alreadyInstalledSensors ?? []
            if (existingSensors.length > 0) {
                const formatted = existingSensors.map((s) => `${formatSensor(s)}`)
                return `This vendor serial number is already in use by the following sensor(s): ${formatted.join('; ')}`
            }

            if (checkResult.notReadyForOnboarding) {
                return 'This vendor serial number is not ready for onboarding. Contact IIoT@Equinor team.'
            }

            return null
        } catch (e) {
            const { displayError } = getErrorMessage(
                e as AxiosError<ResponseObject>,
                'Could not validate vendor serial number. Please try again.'
            )
            return displayError
        }
    }

    const handleNext = useCallback(async () => {
        if (activeStep === 0) {
            setErrorFormVendorSerialNumber(null)
            if (!firstStepComplete()) {
                setShowErrorSelectionBar(true)
                return
            }
            if (!selectedPlant && plantList.length > 0) setSelectedPlant(plantList[0])
            if (!selectedDevice && deviceList.length > 0) setSelectedDevice(deviceList[0])
            if (!selectedProject && projectList.length > 0) setSelectedProject(projectList[0])

            if (!isOffline) {
                const vendorSerialNumberError = await validateVendorSerialNumber()
                if (vendorSerialNumberError) {
                    setErrorFormVendorSerialNumber(vendorSerialNumberError)
                    setShowErrorSelectionBar(true)
                    return
                }
            }

            setShowErrorSelectionBar(false)
        }

        if (activeStep === 1) {
            const {
                SuperiorTag,
                Area,
                Coordinates,
                Tag,
                VendorSerialNumber,
                UserAddedDescription,
                CoordinatesNotAvailable,
                SensorDbKey,
            } = inputValues

            if (Coordinates && !COORDINATES_REGEX.test(Coordinates)) {
                setShowErrorForm(true)
                return
            }

            const existingIndex = sensorData.findIndex((s) => s.vendorSerialNumber === VendorSerialNumber)

            if (existingIndex !== -1) {
                const updated = [...sensorData]
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    superiorTag: SuperiorTag,
                    area: Area,
                    coordinates: Coordinates,
                    tag: Tag,
                    vendorSerialNumber: VendorSerialNumber,
                    userAddedDescription: UserAddedDescription,
                    sensorDbKey: SensorDbKey,
                    coordinatesNotAvailable: CoordinatesNotAvailable,
                }
                updateSensorData(updated)
            } else {
                const newSensor: Partial<Sensor> = {
                    superiorTag: SuperiorTag,
                    area: Area,
                    coordinates: Coordinates,
                    tag: Tag,
                    vendorSerialNumber: VendorSerialNumber,
                    userAddedDescription: UserAddedDescription,
                    sensorDbKey: SensorDbKey,
                    coordinatesNotAvailable: CoordinatesNotAvailable,
                }
                addSensorData(newSensor)
            }
        }

        if (activeStep === 2) {
            try {
                if (inputValues.ExistingImages.length != 0) {
                    inputValues.ExistingImages.forEach((image) => {
                        if (!image.description) {
                            throw new Error('Description is invalid for one or more updated images')
                        }
                    })
                }

                if (images.length === 0) {
                    inputValues.ImageFiles = []
                    inputValues.ImageDescriptions = []
                }

                images.forEach((file, index) => {
                    const description = descriptions[index]

                    if (!description.trim()) {
                        throw new Error(`Description is required for image ${index + 1}`)
                    }

                    inputValues.ImageFiles = images
                    inputValues.ImageDescriptions = descriptions
                })
            } catch (e) {
                setShowImageErrors(true)
                return
            }
        }

        setActiveStep(activeStep + 1)
    }, [
        activeStep,
        selectedPlant,
        selectedDevice,
        selectedProject,
        inputValues,
        sensorData,
        plantList,
        deviceList,
        projectList,
        setSelectedPlant,
        setSelectedDevice,
        setSelectedProject,
        setActiveStep,
        updateSensorData,
        addSensorData,
        firstStepComplete,
    ])

    const handleBack = useCallback(() => {
        setShowErrorOverview(false)
        setShowErrorForm(false)
        setErrorFormVendorSerialNumber(null)
        if (activeStep === 1) {
            const currentSensor = sensorData.find(
                (sensor) => sensor.vendorSerialNumber === inputValues.VendorSerialNumber
            )
            if (currentSensor) {
                setInputValues({
                    SuperiorTag: currentSensor.superiorTag || '',
                    Area: currentSensor.area || '',
                    Coordinates: currentSensor.coordinates || '',
                    Tag: currentSensor.tag || '',
                    VendorSerialNumber: currentSensor.vendorSerialNumber || '',
                    ImageFiles: currentSensor.imageFiles || [],
                    ImageDescriptions: currentSensor.imageDescriptions || [],
                    UserAddedDescription: currentSensor.userAddedDescription ?? '',
                    CoordinatesNotAvailable: currentSensor.coordinatesNotAvailable ?? false,
                    SensorDbKey: currentSensor.sensorDbKey,
                    ExistingImages: currentSensor.existingImages || [],
                })
            }
        }
        if (activeStep > 0) {
            setActiveStep(activeStep - 1)
        }
    }, [activeStep, inputValues, sensorData, setInputValues, setActiveStep])

    const updateDescription = () => {
        const { VendorSerialNumber, UserAddedDescription, ImageFiles, ImageDescriptions, ExistingImages } = inputValues
        const existingIndex = sensorData.findIndex((s) => s.vendorSerialNumber === VendorSerialNumber)
        const updated = [...sensorData]
        if (existingIndex !== -1) {
            updated[existingIndex] = {
                ...updated[existingIndex],
                userAddedDescription: UserAddedDescription,
                imageFiles: ImageFiles,
                imageDescriptions: ImageDescriptions,
                existingImages: ExistingImages,
            }
            updateSensorData(updated)
        }
        return updated
    }

    const setDraftStatus = (draft: boolean) => {
        const { VendorSerialNumber } = inputValues
        const existingIndex = sensorData.findIndex((s) => s.vendorSerialNumber === VendorSerialNumber)
        const updated = [...sensorData]
        if (existingIndex !== -1) {
            updated[existingIndex] = {
                ...updated[existingIndex],
                draft: draft,
            }
            updateSensorData(updated)
        }
        return updated
    }

    const handleScanAnother = (draft: boolean) => {
        updateDescription()
        setDraftStatus(draft)
        setScannedData(null)
        resetInputValues()
        setActiveStep(0)
    }

    const handleFinish = useCallback(async () => {
        const updated = updateDescription()
        const sensorToSaveToDraft = updated.find((sensor) => {
            return sensor.draft === true
        })
        if (sensorToSaveToDraft) {
            await commonSaveToDraft(sensorToSaveToDraft)
        }
        const sensorToSave = updated.find((sensor) => {
            return sensor.draft === false || sensor.draft === undefined
        })
        if (sensorToSave) {
            const payload = {
                plant: selectedPlant?.shortName ?? null,
                project: selectedProject?.projectCode ?? null,
                sensorModel: selectedDevice?.key ?? null,
                plantId: selectedPlant?.plantId ?? null,
                sensor: {
                    ...sensorToSave,
                    sensorDbKey: sensorToSave.sensorDbKey ?? null,
                },
            }

            await onboard(payload)
        }
        handleScanAnother(false)
        setSensorData([])
    }, [selectedPlant, selectedProject, inputValues, selectedDevice, sensorData, onboard])

    const commonSaveToDraft = async (sensorToSaveToDraft: Partial<Sensor>): Promise<SaveToDraftResult> => {
        const draftPayload = {
            plant: selectedPlant?.shortName ?? null,
            project: selectedProject?.projectCode ?? null,
            sensorModel: selectedDevice?.key ?? null,
            plantId: selectedPlant?.plantId ?? null,
            sensor: {
                ...sensorToSaveToDraft,
                sensorDbKey: sensorToSaveToDraft.sensorDbKey ?? null,
            },
        }
        return await saveToDraft(draftPayload)
    }

    const handleSaveToDraft = useCallback(async (): Promise<SaveToDraftResult> => {
        const updated = updateDescription()
        const sensorToSaveToDraft = updated.find((sensor) => {
            return sensor.vendorSerialNumber === inputValues.VendorSerialNumber
        })
        let result: SaveToDraftResult = { success: true, queuedOffline: false }
        if (sensorToSaveToDraft) {
            result = await commonSaveToDraft(sensorToSaveToDraft)
        }
        const updatedWithoutDraft = updated.filter((sensor) => {
            return sensor !== sensorToSaveToDraft
        })
        updateSensorData(updatedWithoutDraft)
        setScannedData(null)
        resetInputValues()
        setActiveStep(0)
        setSensorData([])
        return result
    }, [selectedPlant, selectedProject, inputValues, selectedDevice, sensorData, saveToDraft])

    const completeAvailable = () => {
        const coordinatesIsNotSet = !inputValues.CoordinatesNotAvailable && !inputValues.Coordinates
        const isAvailable =
            !coordinatesIsNotSet &&
            inputValues.SuperiorTag &&
            inputValues.Area &&
            inputValues.VendorSerialNumber &&
            selectedPlant &&
            selectedDevice &&
            selectedProject
        return Boolean(isAvailable)
    }

    const draftAvailable = () => {
        return inputValues.VendorSerialNumber
    }

    const handleFinishProcessClick = async () => {
        if (!completeAvailable()) {
            setShowErrorOverview(true)
            return
        }
        setShowErrorOverview(false)
        await handleFinish()
        const serialNumber = inputValues.VendorSerialNumber
        setCreatedSensorSerialNumber(serialNumber)
    }

    const handleSaveToDraftClick = async () => {
        const serialNumber = inputValues.VendorSerialNumber
        const result = await handleSaveToDraft()
        onClose()
        setDraftQueuedOffline(result.queuedOffline)
        if (result.success) {
            setDraftError(null)
            setDraftSuccessSerialNumber(serialNumber)
        } else {
            setDraftError(result.errorMessage ?? 'An error occurred while saving the draft. Please try again.')
            setDraftSuccessSerialNumber(serialNumber)
        }
    }

    const handleCloseDraftSuccess = () => {
        setDraftSuccessSerialNumber(null)
        setDraftError(null)
        if (!draftError) {
            setSelectedStatus([{ displayName: 'Drafted sensors', value: InstalledSensorStatus.Draft }])
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilteredTags],
            })
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.OnboardedSensors],
            })
        }
        onClose()
    }

    const handleGoToDraft = async () => {
        const serialNumber = draftSuccessSerialNumber
        setIsNavigatingToDraft(true)
        try {
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilteredTags],
            })
            await queryClient.refetchQueries({
                queryKey: [QueryKeys.OnboardedSensors],
            })
            setDraftSuccessSerialNumber(null)
            if (serialNumber && onOpenDraft) {
                onOpenDraft(serialNumber, selectedPlant?.shortName ?? undefined)
            } else {
                onClose()
            }
        } finally {
            setIsNavigatingToDraft(false)
        }
    }

    const handleAddAnotherSensor = () => {
        setDraftSuccessSerialNumber(null)
        setDraftError(null)
        resetInputValues()
        setActiveStep(0)
        onReopen?.()
        queryClient.invalidateQueries({
            queryKey: [QueryKeys.FilteredTags],
        })
    }

    const handleGoToCreatedSensor = async () => {
        const serialNumber = createdSensorSerialNumber
        setIsNavigatingToCreatedSensor(true)
        try {
            queryClient.invalidateQueries({
                queryKey: [QueryKeys.FilteredTags],
            })
            await queryClient.refetchQueries({
                queryKey: [QueryKeys.OnboardedSensors],
            })
            setCreatedSensorSerialNumber(null)
            if (serialNumber && onOpenCreatedSensor) {
                onOpenCreatedSensor(serialNumber, selectedPlant?.shortName ?? undefined)
            } else {
                onClose()
            }
        } finally {
            setIsNavigatingToCreatedSensor(false)
        }
    }

    const handleCloseSensorSuccess = () => {
        setCreatedSensorSerialNumber(null)
        onClose()
        queryClient.invalidateQueries({
            queryKey: [QueryKeys.FilteredTags],
        })
        queryClient.invalidateQueries({
            queryKey: [QueryKeys.OnboardedSensors],
        })
    }

    const handleAddAnotherSensorFromCreated = () => {
        setCreatedSensorSerialNumber(null)
        resetInputValues()
        setActiveStep(0)
        onReopen?.()
        queryClient.invalidateQueries({
            queryKey: [QueryKeys.FilteredTags],
        })
    }

    const handleRetryDraft = () => {
        setDraftSuccessSerialNumber(null)
        setDraftError(null)
        onReopen?.()
    }

    return {
        handleNext,
        handleBack,
        handleScanAnother,
        activeStep,
        setActiveStep,
        resetInputValues,
        isDraftLoading,
        isLoading,
        showErrorOverview,
        draftSuccessSerialNumber,
        draftError,
        createdSensorSerialNumber,
        draftAvailable,
        handleFinishProcessClick,
        handleSaveToDraftClick,
        handleCloseDraftSuccess,
        handleGoToDraft,
        handleAddAnotherSensor,
        handleGoToCreatedSensor,
        handleCloseSensorSuccess,
        handleAddAnotherSensorFromCreated,
        handleRetryDraft,
        isNavigatingToDraft,
        isNavigatingToCreatedSensor,
        draftQueuedOffline,
        isOnboardSuccess: isSuccess,
        isOnboardError: isError,
        isOnboardQueuedOffline: isQueuedOffline,
    }
}
